import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportingLeLensPath } from '~/consts/reportingLeLensApi';

import type { LensCard, LensCardData, LensFilterValues } from '~/types/leLens';

import { encodeRequestBody } from '~/utils/encodeRequestBody';
import { expandLensCardResponse } from '~/utils/leLensTransport';
import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { lensDataQueryKeyPrefix } from './useGetReportingLeLensData';

type PreviewRequest = { lensId: string; version: number; card: LensCard; filters: LensFilterValues };

export function useLensCardPreview() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ lensId, ...body }: PreviewRequest) => {
      const response = await requestWithTimeout<Parameters<typeof expandLensCardResponse>[0]>(
        `${resolveReportingLeLensUrl(reportingLeLensPath(lensId))}/preview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(encodeRequestBody(body)),
        },
        'The preview did not finish. Try again.',
        90_000,
      );
      return expandLensCardResponse(response);
    },
    retry: false,
  });
  return {
    ...mutation,
    keepSavedResult: (lensId: string, filters: LensFilterValues, card: LensCardData) => {
      client.setQueriesData<{ cards: LensCardData[]; filterOptions: Record<string, string[]> }>(
        { queryKey: lensDataQueryKeyPrefix(lensId, filters) },
        (current) =>
          current
            ? { ...current, cards: [...current.cards.filter((entry) => entry.cardId !== card.cardId), card] }
            : current,
      );
    },
  };
}
